// @ts-ignore
import { CSSResultGroup, html, TemplateResult } from 'lit';
// @ts-ignore
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './idb-content-alt-card.css.lit';
import {
  OutlineContentCard
} from '../../base/outline-content-card/outline-content-card';
// @ts-ignore
import { ClassInfo, classMap } from 'lit/directives/class-map.js';

export type ImageTypes = 'image' | 'video-thumbnail' | 'profile';
export type VerticalAlign = 'top' | 'center' | 'bottom';
export type TextVariant = 'default' | 'light' | 'dark' | 'blue';
export type MediaPositions = 'top' | 'left' | 'right';

/**
 * The IDB Image Card Alternative component.
 * @element idb-content-alt-card
 * @extends OutlineContentCard
 */
@customElement('idb-content-alt-card')
export class IdbContentAltCard extends OutlineContentCard {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Whether the image has rounded corners
   */
  @property({ type: Boolean, attribute: 'image-rounded' })
  imageRounded = false;

  /**
   * The type of the image, video-thumbnail, or profile image.
   */
  @property({ type: String, attribute: 'image-type' })
  imageType: ImageTypes;

  /**
   * Vertical Align the content within the card
   */
  @property({ type: String, attribute: 'vertical-content-align' })
  verticalContentAlign: VerticalAlign = 'top';

  /**
   * Whether the card is embedded within an alternating card container.
   *
   * If true, removes left padding on text when image is on the right to align it.
   */
  @property({ type: Boolean, attribute: 'embedded-within-alt-container' })
  embedded = false;

  @property({ type: Boolean, attribute: 'embedded-within-slider' })
  embeddedWithinSlider = false;

  /**
   * The url for the article or media type that creates the link for the card
   */
  @property({ type: String, reflect: true, attribute: 'url' })
  url: string;

  @property({ type: String })
  variant: TextVariant = 'default';

  @property({ type: String, attribute: 'media-position' })
  mediaPosition: MediaPositions = 'top';

  @property({ type: Boolean, attribute: 'is-key-feature' })
  isKeyFeature = false;

  @property({ type: Boolean, attribute: 'is-case-studies' })
  isCaseStudies = false;

  @state() hasVideoSlot: boolean;
  @state() hasSubTitleSlot: boolean;

  firstUpdated(): void {
    this.hasVideoSlot = this.slots.test('video');
    this.hasImageSlot = this.slots.test('image');
    this.hasEyebrowSlot = this.slots.test('eyebrow');
    this.hasTitleSlot = this.slots.test('title');
    this.hasSubTitleSlot = this.slots.test('subtitle');
    this.hasSummarySlot = this.slots.test('summary');
    this.hasContentSlot = this.slots.test('content');
    this.hasCtaSlot = this.slots.test('cta');
  }

  render(): TemplateResult {
    const cardAltClasses = {
      'card': true,
      [`text-variant--${this.variant}`]: this.variant,
      [`media-position--${this.mediaPosition}`]: this.mediaPosition,
      'outline-shadow': this.hasShadow,
      'outline-rounded-2xl': this.isRounded,
      [`outline-bg-${this.cardBackground}`]: this.cardBackground !== undefined,
      [`content-mode--${this.contentMode}`]: this.contentMode,
    };
    const imageClasses = {
      'card-image': true,
      [`image-type--${this.imageType}`]: this.imageType !== undefined,
    };
    const contentWrapperClasses = {
      'card-content-wrapper': true,
      [`content-alignment--${this.contentAlignment}`]: this.contentAlignment,
      [`vertical-content-align--${this.verticalContentAlign}`]:
        this.verticalContentAlign,
    };
    const eyebrowClasses = {
      'card-eyebrow': true,
      [`eyebrow-alignment--${this.eyebrowAlignment}`]: this.eyebrowAlignment,
    };
    const titleClasses = {
      'card-title': true,
      [`title-alignment--${this.titleAlignment}`]: this.titleAlignment,
      [`title-mode--${this.titleMode}`]: this.titleMode,
    };
    const subtitleClasses = {
      'card-subtitle': true,
    };
    const summaryClasses = {
      'card-summary': true,
    };
    const contentClasses = {
      'card-content': true,
    };
    const ctaClasses = {
      'card-cta': true,
      [`cta-alignment--${this.ctaAlignment}`]: this.ctaAlignment,
    };

    return html`
      <section class="${classMap(cardAltClasses)}">
        ${this.hasImageSlot ? this.imageTemplate(imageClasses) : null}
        ${this.hasVideoSlot ? this.videoTemplate() : null}
        <section class="${classMap(contentWrapperClasses)}">
          ${this.eyebrowTemplate(eyebrowClasses)}
          ${this.titleTemplate(titleClasses)}
          ${this.subtitleTemplate(subtitleClasses)}
          ${this.summaryTemplate(summaryClasses)}
          ${this.contentTemplate(contentClasses)}
          ${this.ctaTemplate(ctaClasses)}
        </section>
      </section>
    `;
  }

  subtitleTemplate(classes: ClassInfo): TemplateResult {
    return html`
      <section class="${classMap(classes)}">
        <slot name="subtitle"></slot>
      </section>
    `;
  }

  videoTemplate(): TemplateResult {
    return html`
      <section class="card-video">
        <slot name="video"></slot>
      </section>
    `;
  }

  /*
   * Template partial for the Image slot.
   * @param classes
   * @returns TemplateResult | null
   */
  imageTemplate(classes: ClassInfo): TemplateResult | null {
    if (this.imageType === 'image')
      return html`
      <section class="${classMap(classes)}">
        ${this.slotsFancy.renderInShadow('image')}
      </section>`;
    else if (this.imageType === 'video-thumbnail')
      return html`
      <section class="${classMap(classes)}">
        <idb-icon icon="play-circle" stroke-width="2"></idb-icon>
        <span class="overlay"></span>
        ${this.slotsFancy.renderInShadow('image')}
      </section>`;
    else if (this.imageType === 'profile')
      return html`
      <section image-type="profile" class="${classMap(classes)}">
      ${this.slotsFancy.renderInShadow('image')}
      </section>`;
    return null;
  }
}
